import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress, LinearProgress } from '@material-ui/core';
import * as Styles from './styles.SaveProgress';
// In a .ts file: the build's import lint parses a .tsx imported for a named type without JSX, and fails on it
import { SaveProgressState } from './types.SaveProgress';

interface Props {
    progress: SaveProgressState | null;
}

/**
 * A window in the middle of the screen while orders for several passengers are saved (owner, 2026-11-01):
 * "saving passenger X of Y" and a bar, so a long save does not look like a stuck screen.
 */
const SaveProgress = ({ progress }: Props) => {
    const { t } = useTranslation();
    if (!progress) return null;

    const { done, current, total, isSingleCall } = progress;
    const text = isSingleCall
        ? t('saveProgressAll', { total })
        : t('saveProgressPassenger', { current: Math.min(current, total), total });
    const bar = isSingleCall ? (
        <LinearProgress aria-hidden="true" />
    ) : (
        <LinearProgress variant="determinate" value={total ? (done / total) * 100 : 0} aria-hidden="true" />
    );

    const content = (
        <Styles.Backdrop>
            <Styles.Card role="status" aria-live="polite" dir="rtl">
                <CircularProgress size={36} aria-hidden="true" />
                <Styles.Title>{t('saveProgressTitle')}</Styles.Title>
                <Styles.Text>{text}</Styles.Text>
                <Styles.Bar>{bar}</Styles.Bar>
            </Styles.Card>
        </Styles.Backdrop>
    );

    // On body: the panel slides in with a transform, which would pin a fixed child to the panel
    return ReactDOM.createPortal(content, document.body);
};

export default SaveProgress;
